import {
  Autocomplete,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useGlobalAlert } from "../contexts/alertContext";
import { db } from "../firebase-config";
import { addDoc, collection, getDocs } from "firebase/firestore";
import { LoadingButton } from "@mui/lab";
import { UserContext } from "../contexts/userContext";
import { IProduct, ICategory, IIngredient } from "../interfaces";
import {
  BUSINESS_COLLECTION,
  INGREDIENTS_COLLECTION,
  STOCK_COLLECTION,
} from "../utils/constants";
import TableIngredients from "./TableIngredients";

const CreateProductModal = ({
  show,
  categories,
  product,
  handleClose,
  updateItem,
  addCategory,
}: {
  show: boolean;
  product: IProduct;
  categories: ICategory[];
  handleClose(): void;
  updateItem(item: any): void;
  addCategory(categoryId: string, type: string): void;
}) => {
  const { businessData } = useContext(UserContext);
  const { showAlert } = useGlobalAlert();
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState({} as IProduct);
  const [ingredients, setIngredients] = useState([] as IIngredient[]);
  const [ingredient, setIngredient] = useState(
    {} as {
      id: string;
      name: string;
      quantity_needed: number;
      unit: string;
    }
  );
  const [showIngredients, setShowIngredients] = useState(false);
  const subCollectionRef = collection(
    db,
    BUSINESS_COLLECTION,
    businessData.uid,
    STOCK_COLLECTION
  );

  const handleChange = (name: string, value: any) =>
    setItem({ ...item, [name]: value });

  const getIngredients = async () => {
    const totalIngredients = JSON.parse(
      localStorage.getItem("ingrdients") || "[]"
    );
    if (totalIngredients.length > 0) setIngredients(totalIngredients);
    else {
      const ingredientsCollectionRef = collection(
        db,
        BUSINESS_COLLECTION,
        businessData.uid,
        INGREDIENTS_COLLECTION
      );
      const { docs } = await getDocs(ingredientsCollectionRef);
      setIngredients(
        docs.map((doc) => ({ ...doc.data(), id: doc.id } as IIngredient))
      );
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const totalItem = {
      ...item,
      price: item.price || 0,
      quantity: item.quantity || 0,
      unit_price: item.unit_price || 0,
      status: "enabled",
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    try {
      await addDoc(subCollectionRef, totalItem);
      addCategory(item.category_id, "add");
      showAlert("Producto creado exitosamente", "success");
      handleClose();
    } catch (error) {
      showAlert("Error al crear el producto", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = () => {
    try {
      setLoading(true);
      updateItem({ ...item, updatedAt: new Date() });
      showAlert("Producto actualizado exitosamente", "success");
      handleClose();
    } catch (error) {
      showAlert("Error al actualizar el producto", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddIngredient = () => {
    if (item.ingredients?.length) {
      const list = item.ingredients.concat(ingredient);
      setItem({ ...item, ingredients: list });
    } else setItem({ ...item, ingredients: [ingredient] });
    setIngredient({} as any);
  };

  const handleRemoveIngredient = (idToRemove: string) => {
    const updatedList = item.ingredients?.filter(({ id }) => id !== idToRemove);
    setItem({ ...item, ingredients: updatedList });
  };

  useEffect(() => {
    if (Object.keys(product).length) {
      setIsEdit(true);
      setItem(product);
      if (product.ingredients?.length) setShowIngredients(true);
    }
    return () => {
      setIsEdit(false);
      setItem({} as IProduct);
    };
  }, [product]);

  useEffect(() => {
    if (showIngredients || (isEdit && product.ingredients)) getIngredients();
  }, [showIngredients]);

  return (
    <Dialog onClose={handleClose} open={show} maxWidth="md" fullWidth>
      <IconButton onClick={handleClose} className="close-icon">
        <span className="material-symbols-rounded">close</span>
      </IconButton>
      <DialogTitle>Nuevo producto</DialogTitle>
      <Grid container sx={{ p: 3 }} spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            name="code"
            label="Codigo (opcional)"
            placeholder="Codigo"
            size="small"
            value={item.code || ""}
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            required
            fullWidth
            name="name"
            label="Nombre"
            placeholder="Nombre del producto"
            size="small"
            value={item.name || ""}
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth size="small">
            <Autocomplete
              value={
                { name: item.category_name, id: item.category_id } as ICategory
              }
              options={categories}
              getOptionLabel={(cat) => cat.name || ""}
              size="small"
              onChange={(_: any, newInputValue: any) => {
                if (newInputValue) {
                  setItem({
                    ...item,
                    category_id: newInputValue.id,
                    category_name: newInputValue.name,
                  });
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Categoria" />
              )}
            />
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            required
            fullWidth
            name="quantity"
            label="Cantidad"
            placeholder="Ingrese la cantidad"
            size="small"
            type="number"
            value={item.quantity || ""}
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="type-select">Tipo</InputLabel>
            <Select
              labelId="type-select"
              id="demo-select"
              name="type"
              value={item.type || ""}
              label="Tipo"
              onChange={({ target: { name, value } }) =>
                handleChange(name, value)
              }
            >
              <MenuItem value="counter">Barra</MenuItem>
              <MenuItem value="kitchen">Cocina</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            required
            fullWidth
            name="unit_price"
            label="Precio unitario"
            placeholder="Ingrese un precio"
            size="small"
            type="number"
            value={item.unit_price || ""}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            required
            fullWidth
            name="price"
            label="Precio publico"
            placeholder="Ingrese un precio"
            size="small"
            type="number"
            value={item.price || ""}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              },
            }}
            onChange={({ target: { name, value } }) =>
              handleChange(name, value)
            }
          />
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={item.isPriceVariable || false}
                onClick={() =>
                  handleChange("isPriceVariable", !item.isPriceVariable)
                }
              />
            }
            label="Precio variable"
          />
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showIngredients}
                onClick={() => setShowIngredients((prevState) => !prevState)}
              />
            }
            label="Tiene ingredientes"
          />
        </Grid>

        {showIngredients && (
          <Grid container width="100%" spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <Autocomplete
                  value={ingredient as any}
                  options={ingredients}
                  getOptionLabel={(option) => option.name || ""}
                  size="small"
                  onChange={(_: any, newInputValue: any) => {
                    if (newInputValue) {
                      setIngredient({
                        id: newInputValue.id,
                        name: newInputValue.name,
                      } as any);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Ingrediente" />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                required
                fullWidth
                name="quantity_needed"
                label="Cantidad necesaria"
                placeholder="Ingrese cantidad"
                size="small"
                type="number"
                value={ingredient.quantity_needed || ""}
                onChange={({ target: { name, value } }) =>
                  setIngredient({ ...ingredient, [name]: Number(value) })
                }
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                disabled={!Object.keys(ingredient).length}
                onClick={handleAddIngredient}
              >
                Agregar
              </Button>
            </Grid>
            {item.ingredients && item.ingredients?.length > 0 && (
              <TableIngredients
                ingredients={item.ingredients}
                removeIngredient={handleRemoveIngredient}
              />
            )}
          </Grid>
        )}

        <Grid
          container
          display="flex"
          justifyContent="end"
          alignItems="center"
          size={{ xs: 12 }}
          sx={{ mt: 3 }}
          spacing={1}
        >
          <Button
            color="secondary"
            variant="outlined"
            size="small"
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <LoadingButton
            loading={loading}
            variant="contained"
            color="secondary"
            disabled={
              loading || !Object.keys(item).length || !categories.length
            }
            size="small"
            onClick={() => (isEdit ? handleUpdate() : handleSubmit())}
          >
            {isEdit ? "Editar" : "Crear"}
          </LoadingButton>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default CreateProductModal;
